import * as React from 'react'
import { useRef, useState } from 'react'

import { useDividendsList } from './hooks'
import { defaultFilterItems, DividendEvent, FilterItems } from './types'
import { CCardBody } from '@coreui/react-pro'
import {
  Button,
  DatePicker,
  Input,
  InputRef,
  Space,
  Table,
  Tag,
  TimeRangePickerProps,
} from 'antd'
import { toPath } from '../../../../features/router'
import { path } from '../../../../libs'
import { SearchOutlined } from '@ant-design/icons'
import { FilterConfirmProps } from 'antd/es/table/interface'
import Highlighter from 'react-highlight-words'
import { ColumnsType } from 'antd/lib/table'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import moment from 'moment'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

type DataIndex = keyof DividendEvent

export const DividendsList = () => {
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)
  const { dividends, loading, filterItems } = useDividendsList()

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ]

  const getColumnSearchDateProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          style={{ marginBottom: 8, display: 'flex' }}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e ? [e] : [])}
          presets={rangePresets}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      dayjs.extend(isBetween)
      return record[dataIndex]
        ? dayjs(record[dataIndex]).isBetween(dayjs(value[0]), dayjs(value[1]))
        : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<DividendEvent> = [
    {
      title: 'ID',
      dataIndex: 'dividendEventID',
      sorter: (a, b) => +a.dividendEventID - +b.dividendEventID,
      ...getColumnSearchProps('dividendEventID'),
    },
    {
      title: 'PropertyID',
      dataIndex: 'propertyId',
      filters: filterItems.propertyId,
      filterSearch: true,
      sorter: (a, b) => +a.propertyId - +b.propertyId,
      onFilter: (value: any, record) =>
        record.propertyId.toString().includes(value),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      filters: filterItems.name,
      filterSearch: true,
      onFilter: (value: any, record) => record.name.includes(value),
      // ...getColumnSearchProps('name'),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      sorter: (a, b) => +a.stage - +b.stage,
      render: (item, record) => (
        <>
          {item} / {record.stages}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: filterItems.status,
      filterSearch: true,
      defaultFilteredValue: ['created', 'executable'],
      onFilter: (value: any, record) => record.status.includes(value),
      render: (item: string) => (
        <>
          <Tag color={item == 'completed' ? '#2eb85c' : '#9e9cad'}>{item}</Tag>
        </>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
    },
    {
      title: 'PaymentDate',
      dataIndex: 'paymentDate',
      ...getColumnSearchDateProps('paymentDate'),
      render: (item: string) => (
        <>
          <Tag>{new Date(item).toLocaleString()}</Tag>
        </>
      ),
      sorter: (a, b) => +a.paymentDate - +b.paymentDate,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      filters: [
        { value: 'rentalDividends', text: 'rentalDividends' },
        { value: 'propertyAppreciation', text: 'propertyAppreciation' },
      ],
      filterSearch: true,
      onFilter: (value: any, record) => record.type.includes(value),
    },
    {
      title: 'AnnualDividends',
      dataIndex: 'annualDividends',
      render: (item) => <>{item}%</>,
    },
    {
      title: 'FeeTotal',
      dataIndex: 'feeTotal',
      render: (item) => <>{item}%</>,
    },
    {
      title: 'CreatedAt',
      dataIndex: 'createdAt',
      ...getColumnSearchDateProps('createdAt'),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: 'UpdatedAt',
      dataIndex: 'updatedAt',
      ...getColumnSearchDateProps('updatedAt'),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: (a, b) => moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
    },
    {
      title: 'UpdatedBy',
      dataIndex: 'updatedByUsername',
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={dividends}
        bordered={true}
        scroll={{ x: 2000, y: 500 }}
        loading={loading}
        rowKey="dividendEventID"
        columns={columns}
        pagination={false}
        onRow={(record, rowIndex) => {
          return {
            onClick: () => {
              toPath(path.nova.dividendEvents.edit(record.dividendEventID))
            },
          }
        }}
      ></Table>
    </CCardBody>
  )
}
